import { NgClass, KeyValuePipe } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ConfigService } from 'app/modules/shared/services/config.service';
import { MessageService } from 'primeng/api';
import type { PaginatorState } from 'primeng/paginator';
import { ButtonModule } from 'primeng/button';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { PaginatorModule } from 'primeng/paginator';
import { AppIcons } from '../../../../shared/app.icons';
import { AppSettings } from '../../../../shared/app.settings';
import { CommonBindingDataService } from '../../../../shared/services/common-binding-data.service';
import { EntityService } from '../../../../shared/services/entity.service';
import { MetaDataService } from 'app/modules/shared/services/meta-data.service';
import { TabViewModule } from 'primeng/tabview';
import * as _ from 'lodash';
import { NoResultFoundComponent } from 'app/modules/shared/components/no-result-found/no-result-found.component';
import type { AttributeData, AttributeDataTab, FluffyGroup } from '../../../../shared/models/attribute.models';
import type { ListResponse } from '../../../../shared/models/listResponse.models';

interface DropdownItem {
  name: string;
  code: string;
}

interface TrialDetails {
  entityId: string;
  [key: string]: unknown;
}

interface ProtocolRowData {
  [key: string]: unknown;
}

interface DriverList {
  [key: string]: unknown;
}

interface ProtocolFile {
  id?: string;
  entity_name: string;
  [key: string]: unknown;
}

@Component({
  selector: 'app-overview-details',
  standalone: true,
  imports: [ButtonModule, TranslateModule, OverlayPanelModule, FormsModule,
    NgClass, TabViewModule, KeyValuePipe, NoResultFoundComponent, PaginatorModule],
  templateUrl: './overview-details.component.html',
  styleUrl: './overview-details.component.scss'
})
export class OverviewDetailsComponent implements OnInit, OnChanges {
  miIcons = AppIcons;
  iconPos: 'left' | 'right' = 'right';
  @Input() attributeValues: Record<string, unknown> & {
    sponsor_details?: {
      sponsor_name?: string;
      [key: string]: unknown;
    };
  };
  @Input() attributeLabels: Record<string, string>;
  @Input() trialAttributeData: AttributeData;
  @Input() protocolEntityId: string;
  @Input() protocolRowData: ProtocolRowData;
  @Input() trialDetails: TrialDetails;
  @Input() driverList: DriverList[];
  @Input() isDeleted: boolean = false;
  @Input() isUnassigned: boolean = false;
  @Output() onToggleOverLay: EventEmitter<boolean> = new EventEmitter();
  @Input() isAllowToEdit: boolean = false;
  protocolEntityIdField: { attributeId: string;[key: string]: unknown };
  protocolEntityNameField: { attributeId: string;[key: string]: unknown };  
  selectedItem: DropdownItem[] = [];
  data: unknown;
  options: unknown;
  tab: AttributeDataTab;
  details: FluffyGroup;
  center: google.maps.LatLngLiteral = {
    lat: 24,
    lng: 12,
  };
  zoom = 4;
  map: google.maps.Map;
  bulkRowCount = 1;
  barcodeImage: string;
  visiblePrintLabel: boolean = false;
  rowData: ProtocolRowData;
  isLoading: boolean = false;
  selectedEventId: string;
  eventLat: number;
  eventLng: number;
  marker: google.maps.Marker;
  regionGeofences: Array<{ id: string; name: string }> = [];
  zoneIds: string[] = [];
  fields: Array<{ attributeCode: string;[key: string]: unknown }>;
  regionGeofence: { id: string; name: string };
  matchedZoneDisplayName: string = '';
  openLogo: string = "../../../../../../assets/images/mi-theme/light/nav/pdf_icon.png";
  protocolFiles: ProtocolFile[] = [];
  filterAttributeLabels: Record<string, string>;
  filterList: Array<{ attributeCode: string; attributeId: string;[key: string]: unknown }>;
  protocolFilesData: ProtocolFile[] = [];
  protocolProductFilesData: ProtocolFile[] = [];
  protocolPurchaseOrderFilesData: ProtocolFile[] = [];
  protocolFilesPageFirst = 0;
  protocolFilesPageRows = AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT;
  protocolFilesTotalRecords = 0;
  protocolProductFilesPageFirst = 0;
  protocolProductFilesPageRows = AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT;
  protocolProductFilesTotalRecords = 0;
  protocolPurchaseOrderFilesPageFirst = 0;
  protocolPurchaseOrderFilesPageRows = AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT;
  protocolPurchaseOrderFilesTotalRecords = 0;


  constructor(public entityService: EntityService,
    public cs: CommonBindingDataService,
    private messageService: MessageService, private router: Router, public configService: ConfigService,
    public metaDataService: MetaDataService,
    private cd: ChangeDetectorRef,
  ) { }

  ngOnChanges() {
    this.rowData = this.protocolRowData;

    if (this.protocolEntityId && this.protocolEntityIdField && this.protocolEntityNameField) {
      this.protocolFilesPageFirst = 0;
      this.protocolProductFilesPageFirst = 0;
      this.protocolPurchaseOrderFilesPageFirst = 0;
      this.searchProtocolFiles();
      this.searchProtocolProductsFiles();
      this.searchProtocolPurchaseOrderFiles();
    }
  }
  onProtocolFilesPageChange(event: PaginatorState) {
    this.protocolFilesPageFirst = event.first ?? 0;
    this.protocolFilesPageRows = event.rows ?? AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT;
    this.searchProtocolFiles();
  }
  onProtocolProductFilesPageChange(event: PaginatorState) {
    this.protocolProductFilesPageFirst = event.first ?? 0;
    this.protocolProductFilesPageRows = event.rows ?? AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT;
    this.searchProtocolProductsFiles();
  }
  onProtocolPurchaseOrderFilesPageChange(event: PaginatorState) {
    this.protocolPurchaseOrderFilesPageFirst = event.first ?? 0;
    this.protocolPurchaseOrderFilesPageRows = event.rows ?? AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT;
    this.searchProtocolPurchaseOrderFiles();
  }
  searchProtocolProductsFiles() {
    const searchParams = {
      limit: this.protocolProductFilesPageRows,
      offset: this.protocolProductFilesPageFirst,
      searchStr: "",
      filters: [{
        attributeId: this.protocolEntityIdField.attributeId,
        attributeValue: this.protocolEntityId
      }, {
        attributeId: this.protocolEntityNameField.attributeId,
        attributeValue: 'protocol_product'
      }],
      countryCode: '',
      deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
      forTenantCode: this.configService.getForTenantCode(),
      actionStatus: ''
    };
    const country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    searchParams.countryCode = country[0].countryCode;
    this.entityService.searchEntity(AppSettings.ENTITY_CODE.PROTOCOL_FILE, searchParams, true).subscribe({
      next: (response: ListResponse) => {
        this.protocolProductFilesTotalRecords = typeof response?.count === 'number' ? response.count : 0;
        this.protocolProductFilesData = this.cs.getTableData(response) || [];
        this.cd.detectChanges();
      },
      error: () => {
        this.protocolProductFilesTotalRecords = 0;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: this.cs.getLabel('lbl_error_fetching_protocol_products_files') });
      }
    });
  }
  searchProtocolPurchaseOrderFiles() {
    const searchParams = {
      limit: this.protocolPurchaseOrderFilesPageRows,
      offset: this.protocolPurchaseOrderFilesPageFirst,
      searchStr: "",
      filters: [{
        attributeId: this.protocolEntityIdField.attributeId,
        attributeValue: this.protocolEntityId
      }, {
        attributeId: this.protocolEntityNameField.attributeId,
        attributeValue: 'protocol_purchase_order'
      }],
      countryCode: '',
      deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
      forTenantCode: this.configService.getForTenantCode(),
      actionStatus: ''
    };
    const country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    searchParams.countryCode = country[0].countryCode;
    this.entityService.searchEntity(AppSettings.ENTITY_CODE.PROTOCOL_FILE, searchParams, true).subscribe({
      next: (response: ListResponse) => {
        this.protocolPurchaseOrderFilesTotalRecords = typeof response?.count === 'number' ? response.count : 0;
        this.protocolPurchaseOrderFilesData = this.cs.getTableData(response) || [];
        this.cd.detectChanges();
      },
      error: () => {
        this.protocolPurchaseOrderFilesTotalRecords = 0;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: this.cs.getLabel('lbl_error_fetching_protocol_purchase_order_files') });
      }
    });
  } 
  searchProtocolFiles() {
    const searchParams = {
      limit: this.protocolFilesPageRows,
      offset: this.protocolFilesPageFirst,
      searchStr: "",
      filters: [{
        attributeId: this.protocolEntityIdField.attributeId,
        attributeValue: this.protocolEntityId
      }, {
        attributeId: this.protocolEntityNameField.attributeId,
        attributeValue: 'protocol'
      }],
      countryCode: '',
      deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
      forTenantCode: this.configService.getForTenantCode(),
      actionStatus: ''
    };
    const country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    searchParams.countryCode = country[0].countryCode;
    this.entityService.searchEntity(AppSettings.ENTITY_CODE.PROTOCOL_FILE, searchParams, true).subscribe({
      next: (response: ListResponse) => {
        this.protocolFilesTotalRecords = typeof response?.count === 'number' ? response.count : 0;
        this.protocolFiles = this.cs.getTableData(response) || [];
        this.protocolFilesData = this.protocolFiles.filter(item => item.entity_name === 'protocol');
        this.cd.detectChanges();
      },
      error: () => {
        this.protocolFilesTotalRecords = 0;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: this.cs.getLabel('lbl_error_fetching_protocol_files') });
      }
    });
  }

  ngOnInit() {
    // if (this.trialAttributeData && this.trialAttributeData.tabs) {
    //   this.tab = this.trialAttributeData.tabs.find(ele => ele.tabCode === 'overview');
    // }
    // if (this.tab && this.tab.groups) {
    //   this.details = this.tab.groups.find(ele => ele.code === 'details');
    // }

    this.getFilterView();
  }

  getFilterView() {
    this.entityService.getAttributeDefinition(AppSettings.ENTITY_CODE.PROTOCOL_FILE, AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW).subscribe(filterResponse => {
      if (filterResponse) {
        this.setProtocolFilterView(filterResponse);
      }
    });
  }

  setProtocolFilterView(filterResponse: unknown) {
    this.filterAttributeLabels = this.cs.getAttributeLabels(filterResponse) as unknown as Record<string, string>;
    this.filterList = this.cs.getColumns(filterResponse) as Array<{ attributeCode: string; attributeId: string;[key: string]: unknown }>;
    this.filterList = _.sortBy(this.filterList, 'attributeIndex');
    this.protocolEntityIdField = this.filterList.find(field => field.attributeCode === 'protocol_entity_id');
    this.protocolEntityNameField = this.filterList.find(field => field.attributeCode === 'entity_name');
    this.searchProtocolFiles();
    if (this.protocolEntityId && this.protocolEntityIdField && this.protocolEntityNameField) {
      this.searchProtocolProductsFiles();
      this.searchProtocolPurchaseOrderFiles();
    }
  }

  generateTemplate() {
    const country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    const labelQuantity = 1;
    const labelSize = 'small';
    const data = {
      forTenantCode: this.configService.getForTenantCode(),
      entityCode: AppSettings.ENTITY_CODE.PROTOCOL,
      countryCode: country[0].countryCode,
      entityIds: [this.trialDetails.entityId],
      labelQuantity,
      labelSize
    };
    this.isLoading = true;
    this.entityService.printLabels(data.entityCode, data).subscribe(res => {
      this.messageService.add({ severity: 'success', summary: 'Success', detail: this.cs.getLabel('lbl_file_download_successfully') });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res);
      a.download = 'Vehicle-QR-Sticker.pdf';
      a.click();
      this.visiblePrintLabel = false;
      this.isLoading = false;
    },
      () => {
        // Handle error silently
      });
  }

  showPrintLabelDialog() {
    this.visiblePrintLabel = true;
  }

  scrollToTop() {

    const tabviewPanels = document.querySelector('.p-tabview-panels');
    if (tabviewPanels) {
      tabviewPanels.scrollTop = 0;
    }
  }

  redirectToEdit() {
    this.router.navigate([`/app/trials/add-trial/${this.trialDetails.entityId}`]);
  }

  downloadFile(url: string, fileName: string) {
    fetch(url, { method: 'GET' })
      .then(response => response.blob())
      .then(blob => {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
      });
  }
}

